import { Formik, ErrorMessage, useFormikContext } from "formik";
import * as yup from "yup";
import DateInput from "components/common/date-picker";
import Input from "components/common/input";
import SelectComponent from "components/common/select";
import { useCallback, useEffect, useState, useRef } from "react";
import {
  getCities,
  getCounties,
  getHealthPlans,
  getStates,
} from "containers/policies/api";
import { getOrganizations } from "containers/community/api";
import {
  CONDITIONS,
  CURRENT_LIVING_SITUATION,
  DEBOUNCE_DELAY,
  GENDER,
  initialMetaForTable,
  MARTIAL_STATUSES,
  POPULATION_FOCUSES,
  programTypes,
  RACES,
  REFERRAL_STATUS,
  referralProgramOptions,
  referringProviderOptions,
  US_STATES,
} from "constants/common";
import { debounce } from "lodash";
import AsyncSelect from "components/common/select/AsyncSelect";
import {
  US_PHONE_REGEX,
  US_STATES as US_STATES_VALIDATION,
} from "constants/members";
import { normalizeInput } from "constants/common";
import {
  useDebouncedCitiesDataCallback,
  useDebouncedCountiesDataCallback,
  useDebouncedHealthPlansDataCallback,
  useDebouncedStatesDataCallback,
  useDebouncedZipCodesDataCallback,
} from "utils/debouncedHelper";
import {
  capitalizeName,
  disableFutureDates,
  formatLocation,
} from "utils/common";
import moment from "moment";
import { getZipCodes } from "containers/leadcaremanager/api";
import { toast } from "react-toastify";
import CustomToast from "components/common/custom-toast";
import { alertTypes } from "constants/common";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
function getExtension(filename) {
  if (!filename) return "";
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

const AddressSync = ({
  isMailingSameAsHome,
  fetchMailingCounties,
  fetchMailingCities,
  fetchMailingZipCodes,
  mailingCountiesList,
  mailingCitiesList,
  mailingZipCodesList,
}) => {
  const { values, setFieldValue } = useFormikContext();

  const syncMailingAddress = () => {
    setFieldValue("mailing_address", values.home_address);
    setFieldValue("mailing_state_id", values.state_id);
    setFieldValue("mailing_county_id", values.county_id);
    setFieldValue("mailing_city_id", values.city_id);
    setFieldValue("mailing_zip_code_id", values.zip_code_id);

    // Only fetch if the data is not already available
    if (values.state_id?.value && mailingCountiesList.length === 0) {
      fetchMailingCounties(values.state_id.value);
    }
    if (
      values.state_id?.value &&
      values.county_id?.value &&
      mailingCitiesList.length === 0
    ) {
      fetchMailingCities(values.state_id.value, values.county_id.value);
    }
    if (
      values.state_id?.value &&
      values.county_id?.value &&
      values.city_id?.value &&
      mailingZipCodesList.length === 0
    ) {
      fetchMailingZipCodes(
        values.state_id.value,
        values.county_id.value,
        values.city_id.value
      );
    }
  };

  useEffect(() => {
    if (isMailingSameAsHome) {
      syncMailingAddress();
    }
  }, [
    isMailingSameAsHome,
    values.home_address,
    values.state_id,
    values.county_id,
    values.city_id,
    values.zip_code_id,
    mailingCountiesList,
    mailingCitiesList,
    mailingZipCodesList,
  ]);

  return null;
};

const referralWithMemberInitialValues = {
  // Personal Info
  first_name: "",
  last_name: "",
  date_of_birth: "",
  gender: "",
  ehtnicity: "",
  martial_status: "",

  // Membership Info
  cin: "",
  aica_account: "",
  health_plan_id: "",
  if_number: "",

  // Contact Info
  phone_number: "",
  email: "",
  home_address: "",
  mailing_address: "",
  state_id: "",
  city_id: "",
  county_id: "",
  zip_code_id: "",
  mailing_state_id: "",
  mailing_city_id: "",
  mailing_county_id: "",
  mailing_zip_code_id: "",

  // Communication Preferences
  preferred_spoken_lang: "",
  preferred_written_lang: "",

  // Other
  population_focus: [],
  conditions: [],

  // Referral Information
  referral_contact_id: "",
  referring_provider: "",
  referral_programe: "",
  referral_source: "",
  community_engagement_id: "",
  programe_enrollement: [],
  referral_status: "",
  denial_reason: "",
  referral_if_number: "",
  current_living_situation: "",
  attachment: null,

  // Authorization Information
  auth_number: "",
  start_date: "",
  end_date: "",
  approved_days: "",
  content_type: "",
  file_name: "",
};

const referralWithMemberValidationSchema = yup.object().shape({
  // Personal Info
  first_name: yup.string().required("First Name is required"),
  last_name: yup.string().required("Last Name is required"),
  date_of_birth: yup.date().required("Date of Birth is required"),
  gender: yup.string().required("Gender is required"),

  // Membership Info
  cin: yup.string().required("CIN is required"),
  aica_account: yup.string(),
  health_plan_id: yup.string().required("Health Plan is required"),

  // Contact Info
  phone_number: yup
    .string()
    .matches(US_PHONE_REGEX, "Phone number is not valid *")
    .required("Phone Required")
    .nullable(),
  email: yup.string().email(),
  home_address: yup.string().required("Home Address is required"),
  city_id: yup.mixed(),
  state_id: yup.mixed().required("Home State is required"),
  county_id: yup.mixed().required("Home County is required"),
  zip_code_id: yup.mixed(),
  mailing_address: yup.string().required("Mailing Address is required"),
  mailing_city_id: yup.mixed(),
  mailing_state_id: yup.mixed().required("Mailing State is required"),
  mailing_county_id: yup.mixed().required("Mailing County is required"),
  mailing_zip_code_id: yup.mixed(),

  // Communication Preferences
  preferred_spoken_lang: yup
    .string()
    .required("Preferred Spoken Language is required"),
  preferred_written_lang: yup
    .string()
    .required("Preferred Written Language is required"),

  // Other
  population_focus: yup
    .array()
    .min(1, "Population of Focus is required")
    .required("Population of Focus is required"),
  conditions: yup
    .array()
    .min(1, "Condition is required")
    .required("Condition is required"),

  // Referral
  referring_provider: yup.string().required("Referring provider is required"),
  referral_source: yup.string().required("Referral source is required"),
  programe_enrollement: yup
    .array()
    .min(1, "Program enrollment cannot be empty")
    .required("Program enrollment is required"),
  referral_programe: yup.string().required("Referral program is required"),
  referral_status: yup.string().required("Referral status is required"),
  denial_reason: yup.string().when("referral_status", {
    is: REFERRAL_STATUS.Denied,
    then: yup.string().required("Denial Reason is required"),
    otherwise: yup.string().notRequired(),
  }),
  current_living_situation: yup
    .string()
    .required("Current Living Situation is required"),
  // attachment: yup
  //   .mixed()
  //   .nullable()
  //   .test("fileType", "File must be of type PDF, PNG, or JPEG", (file) => {
  //     if (!file) return true;
  //     const allowedExtensions = ["pdf", "png", "jpeg", "jpg"];
  //     const ext = getExtension(file.name);
  //     return allowedExtensions.includes(ext);
  //   })
  //   .test("fileSize", "File size must be less than 10 MB", (file) => {
  //     if (!file) return true;
  //     return file.size <= MAX_FILE_SIZE;
  //   }),

  // Authorization Information
  auth_number: yup.string().when("referral_status", {
    is: REFERRAL_STATUS.Approved,
    then: yup.string().required("Auth Number is required"),
    otherwise: yup.string().notRequired(),
  }),
  start_date: yup.string().when("referral_status", {
    is: REFERRAL_STATUS.Approved,
    then: yup.string().required("Auth Start Date is required"),
    otherwise: yup.string().notRequired(),
  }),
  end_date: yup.string().when("referral_status", {
    is: REFERRAL_STATUS.Approved,
    then: yup.string().required("Auth End Date is required"),
    otherwise: yup.string().notRequired(),
  }),
  approved_days: yup.string().when("referral_status", {
    is: REFERRAL_STATUS.Approved,
    then: yup.string().required("Number of Approved Days is required"),
    otherwise: yup.string().notRequired(),
  }),
});

const useDebouncedFetchOrganizations = () => {
  const debouncedFetch = useRef(
    debounce(async (inputValue, callback) => {
      try {
        const response = await getOrganizations({
          search: inputValue,
        });

        let formattedOptions = response?.community_engagements?.map((el) => ({
          ...el,
          value: el.id,
          label: el.name,
        }));

        if (!formattedOptions || formattedOptions.length === 0) {
          callback([]);
        } else {
          callback(formattedOptions);
        }
      } catch (error) {
        console.error("Failed to fetch members:", error);
        callback([]);
      }
    }, DEBOUNCE_DELAY)
  ).current;

  return debouncedFetch;
};

const AddFormWithoutMember = ({
  handleSubmitForm,
  handleClose,
  referralData,
}) => {
  const [healthPlansList, setHealthPlansList] = useState([]);
  const [organizationList, setOrganizationList] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [countiesList, setCountiesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [zipCodesList, setZipCodesList] = useState([]);
  const [mailingStatesList, setMailingStatesList] = useState([]);
  const [mailingCountiesList, setMailingCountiesList] = useState([]);
  const [mailingCitiesList, setMailingCitiesList] = useState([]);
  const [mailingZipCodesList, setMailingZipCodesList] = useState([]);
  const [selectedReferralSource, setSelectedReferralSource] = useState("");
  const [isMailingSameAsHome, setIsMailingSameAsHome] = useState(false);

  const debouncedFetchOrganizationData = useDebouncedFetchOrganizations();
  const debouncedHealthPlansDataCallback = useDebouncedHealthPlansDataCallback();
  const debouncedStatesDataCallback = useDebouncedStatesDataCallback();
  const debouncedCountiesDataCallback = useDebouncedCountiesDataCallback();
  const debouncedCitiesDataCallback = useDebouncedCitiesDataCallback();
  const debouncedZipCodesDataCallback = useDebouncedZipCodesDataCallback();
  const [selectedMember, setSelectedMember] = useState("");

  const mailingAddress = selectedMember?.addresses?.find(
    (addr) => addr.address_type === "mailing"
  );
  const homeAddress = selectedMember?.addresses?.find(
    (addr) => addr.address_type === "home"
  );

  const fetchStates = async () => {
    try {
      const result = await getStates({ ...initialMetaForTable });
      if (result?.states) {
        setStatesList(result.states);
        setMailingStatesList(result.states);
      }
    } catch (error) {
      console.error("Failed to fetch states:", error);
    }
  };

  const fetchCounties = async (stateId) => {
    if (!stateId) return;
    try {
      const result = await getCounties({
        ...initialMetaForTable,
        state_id: stateId,
      });
      if (result?.counties) {
        setCountiesList(result.counties);
      }
    } catch (error) {
      console.error("Failed to fetch counties:", error);
    }
  };

  const fetchCities = async (stateId, countyId) => {
    if (!stateId || !countyId) return;
    try {
      const result = await getCities({
        ...initialMetaForTable,
        state_id: stateId,
        county_id: countyId,
      });
      if (result?.cities) {
        setCitiesList(result.cities);
      }
    } catch (error) {
      console.error("Failed to fetch cities:", error);
    }
  };

  const fetchZipCodes = async (stateId, countyId, cityId) => {
    if (!stateId || !countyId || !cityId) return;
    try {
      const result = await getZipCodes({
        ...initialMetaForTable,
        state_id: stateId,
        county_id: countyId,
        city_id: cityId,
      });
      if (result?.zip_codes) {
        setZipCodesList(result.zip_codes);
      }
    } catch (error) {
      console.error("Failed to fetch zip codes:", error);
    }
  };

  const fetchMailingCounties = async (stateId) => {
    if (!stateId) return;
    try {
      const result = await getCounties({
        ...initialMetaForTable,
        state_id: stateId,
      });
      if (result?.counties) {
        setMailingCountiesList(result.counties);
      }
    } catch (error) {
      console.error("Failed to fetch mailing counties:", error);
    }
  };

  const fetchMailingCities = async (stateId, countyId) => {
    if (!stateId || !countyId) return;
    try {
      const result = await getCities({
        ...initialMetaForTable,
        state_id: stateId,
        county_id: countyId,
      });
      if (result?.cities) {
        setMailingCitiesList(result.cities);
      }
    } catch (error) {
      console.error("Failed to fetch mailing cities:", error);
    }
  };

  const fetchMailingZipCodes = async (stateId, countyId, cityId) => {
    if (!stateId || !countyId || !cityId) return;
    try {
      const result = await getZipCodes({
        ...initialMetaForTable,
        state_id: stateId,
        county_id: countyId,
        city_id: cityId,
      });
      if (result?.zip_codes) {
        setMailingZipCodesList(result.zip_codes);
      }
    } catch (error) {
      console.error("Failed to fetch mailing zip codes:", error);
    }
  };

  const fetchHealthPlan = async (searchString = "") => {
    try {
      const result = await getHealthPlans({
        ...initialMetaForTable,
        search: searchString,
      });
      if (result?.health_plans) {
        setHealthPlansList(result.health_plans);
      }
    } catch (error) {
      console.error("Failed to fetch health plans:", error);
    }
  };

  useEffect(() => {
    fetchHealthPlan();
    fetchStates();
  }, []);

  useEffect(() => {
    (async () => {
      const result = await getOrganizations({
        ...initialMetaForTable,
        page: 1,
      });
      if (result?.community_engagements)
        setOrganizationList(result.community_engagements);
    })();
  }, [debouncedFetchOrganizationData]);

  useEffect(() => {
    if (selectedMember?.state?.id) {
      fetchCounties(selectedMember.state.id);
    }
  }, [selectedMember?.state?.id]);

  useEffect(() => {
    if (selectedMember?.state?.id && selectedMember?.county?.id) {
      fetchCities(selectedMember.state.id, selectedMember.county.id);
    }
  }, [selectedMember?.state?.id, selectedMember?.county?.id]);

  useEffect(() => {
    if (
      selectedMember?.state?.id &&
      selectedMember?.county?.id &&
      selectedMember?.city?.id
    ) {
      fetchZipCodes(
        selectedMember.state.id,
        selectedMember.county.id,
        selectedMember.city.id
      );
    }
  }, [
    selectedMember?.state?.id,
    selectedMember?.county?.id,
    selectedMember?.city?.id,
  ]);

  useEffect(() => {
    if (mailingAddress?.state_id) {
      fetchMailingCounties(mailingAddress.state_id);
    }
  }, [mailingAddress?.state_id]);

  useEffect(() => {
    if (mailingAddress?.state_id && mailingAddress?.county_id) {
      fetchMailingCities(mailingAddress.state_id, mailingAddress.county_id);
    }
  }, [mailingAddress?.state_id, mailingAddress?.county_id]);

  useEffect(() => {
    if (
      mailingAddress?.state_id &&
      mailingAddress?.county_id &&
      mailingAddress?.city_id
    ) {
      fetchMailingZipCodes(
        mailingAddress.state_id,
        mailingAddress.county_id,
        mailingAddress.city_id
      );
    }
  }, [
    mailingAddress?.state_id,
    mailingAddress?.county_id,
    mailingAddress?.city_id,
  ]);
  const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  return (
    <Formik
      initialValues={referralData || referralWithMemberInitialValues}
      validationSchema={referralWithMemberValidationSchema}
      onSubmit={async (values, { setErrors }) => {
        let base64Document = null;
        if (values.attachment) {
          base64Document = await convertFileToBase64(values.attachment);
        }
        const {
          programe_enrollement,
          referral_source,
          community_engagement_id,
          referring_provider,
          referral_contact_id,
          referral_programe,
          referral_status,
          denial_reason,
          referral_if_number,
          current_living_situation,
          auth_number,
          start_date,
          end_date,
          approved_days,
          attachment,
          ...restValues
        } = values;

        const {
          home_address,
          city_id,
          state_id,
          county_id,
          zip_code_id,
          mailing_address,
          mailing_city_id,
          mailing_state_id,
          mailing_county_id,
          mailing_zip_code_id,
          population_focus,
          conditions,
          ...filteredValues
        } = restValues;

        const addresses_attributes = [
          {
            ...(homeAddress?.id && {
              id: homeAddress.id,
            }),
            street: home_address,
            city: city_id?.label || "",
            state: state_id?.label || "",
            county: county_id?.label || "",
            zip_code: zip_code_id?.label || "",
            zip_code_id: zip_code_id?.value || "",
            city_id: city_id?.value || "",
            state_id: state_id?.value || "",
            county_id: county_id?.value || "",
            address_type: "home",
          },
          {
            ...(mailingAddress?.id && {
              id: mailingAddress.id,
            }),
            street: mailing_address,
            city: mailing_city_id?.label || "",
            state: mailing_state_id?.label || "",
            county: mailing_county_id?.label || "",
            zip_code: mailing_zip_code_id?.label || "",
            city_id: mailing_city_id?.value || "",
            state_id: mailing_state_id?.value || "",
            county_id: mailing_county_id?.value || "",
            zip_code_id: mailing_zip_code_id?.value || "",
            address_type: "mailing",
          },
        ];

        const referral = {
          programe_enrollement:
            programe_enrollement?.map((item) => item?.value || "") || [],
          referral_source,
          community_engagement_id,
          referring_provider,
          referral_contact_id,
          referral_programe,
          referral_status,
          if_number: referral_if_number,
          attachment,
          current_living_situation,
          denial_reason:
            referral_status === REFERRAL_STATUS.Denied ? denial_reason : "",
          auth_number,
          approved_days,
          start_date: moment(values.start_date).format("YYYY-MM-DD"),
          end_date: moment(values.end_date).format("YYYY-MM-DD"),
          approved_days,
          member_info_jsonb: {
            member: {
              addresses_attributes,
              ...filteredValues,
              city_id: city_id?.value || "",
              state_id: state_id?.value || "",
              county_id: county_id?.value || "",
              zip_code_id: zip_code_id?.value || "",
              population_focus: population_focus
                .map((item) => item.value)
                .join(","),
              conditions: conditions.map((item) => item.value).join(","),
            },
          },
        };
        const payload = {
          referral,
          attachment: base64Document,
          content_type: values?.content_type || "",
          file_name: values?.file_name || "",
        };

        handleSubmitForm(payload, setErrors);
      }}
    >
      {({ handleChange, values, handleSubmit, errors, setFieldValue }) => (
        <div className="question-modal">
          <form className="form-main" onSubmit={handleSubmit}>
            <hr className="custom-modal-line mt-0 " />
            <h4 className="my-4">Member Info</h4>
            <h4 className="my-4">Personal Info</h4>
            <div className="input-flex row">
              <div className="col-sm-4">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. John Doe"
                  type="text"
                  label="First Name"
                  value={values.first_name}
                  name="first_name"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="first_name"
                    />
                  }
                />
              </div>
              <div className="col-sm-4">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. John Doe"
                  type="text"
                  label="Last Name"
                  value={values.last_name}
                  name="last_name"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="last_name"
                    />
                  }
                />
              </div>
              <div className="col-sm-4">
                <DateInput
                  isRequired
                  handleChange={(date) => {
                    setFieldValue("date_of_birth", date);
                  }}
                  value={values.date_of_birth}
                  label="Date of Birth"
                  name="date_of_birth"
                  placeholder="Date of Birth"
                  isValidDate={disableFutureDates}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="date_of_birth"
                    />
                  }
                />
              </div>
            </div>
            <div className="input-flex row">
              <div className="col-sm-4">
                <SelectComponent
                  isRequired
                  label="Gender"
                  options={GENDER}
                  selectedValue={values.gender}
                  placeholder="Select Gender"
                  handleChange={(obj) => {
                    setFieldValue("gender", obj.value);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="gender"
                    />
                  }
                />
              </div>
              <div className="col-sm-4">
                <SelectComponent
                  label="Race/Ethnicity"
                  options={RACES}
                  selectedValue={values.ehtnicity}
                  placeholder="Select Race/Ethnicity"
                  handleChange={(obj) => {
                    setFieldValue("ehtnicity", obj.value);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="ehtnicity"
                    />
                  }
                />
              </div>
              <div className="col-sm-4">
                <SelectComponent
                  label="Marital Status"
                  options={MARTIAL_STATUSES}
                  selectedValue={values.martial_status}
                  placeholder="Select Marital Status"
                  handleChange={(obj) => {
                    setFieldValue("martial_status", obj.value);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="martial_status"
                    />
                  }
                />
              </div>
            </div>
            <h4 className="my-4">Membership Info</h4>
            <div className="input-flex row">
              <div className="col-sm-6">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. CIN7438201"
                  type="text"
                  label="CIN"
                  value={values.cin}
                  name="cin"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="cin"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <Input
                  disabled={selectedMember}
                  handleChange={handleChange}
                  placeholder="e.g. AICA256849317"
                  type="text"
                  label="FHIR ID"
                  value={values.aica_account}
                  name="aica_account"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="aica_account"
                    />
                  }
                />
              </div>
            </div>
            <div className="input-flex row">
              <div className="col-sm-6">
                <AsyncSelect
                  label="Health Plan"
                  isRequired
                  options={healthPlansList.map((el) => ({
                    value: el.id,
                    label: el.name,
                  }))}
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedHealthPlansDataCallback(inputValue, resolve);
                    })
                  }
                  selectedValue={values.health_plan_id}
                  placeholder="Select Health Plan"
                  handleChange={(obj) => {
                    setFieldValue("health_plan_id", obj.value);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="health_plan_id"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <Input
                  handleChange={handleChange}
                  placeholder="e.g. Number"
                  type="text"
                  label="IF Number"
                  value={values.if_number}
                  name="if_number"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="if_number"
                    />
                  }
                />
              </div>
            </div>
            <h4 className="my-4">Contact Info</h4>
            <div className="input-flex row">
              <div className="col-sm-6">
                <Input
                  isRequired
                  handleChange={(e) => {
                    const formattedNumber = normalizeInput(e.target.value);
                    setFieldValue("phone_number", formattedNumber);
                  }}
                  placeholder="e.g. 555-123-4567"
                  type="text"
                  label="Phone"
                  value={values.phone_number}
                  name="phone_number"
                  className="mb-1"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="phone_number"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <Input
                  handleChange={handleChange}
                  placeholder="e.g. johndoe@gmail.com"
                  type="email"
                  label="Email"
                  value={values.email}
                  name="email"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="email"
                    />
                  }
                />
              </div>
            </div>
            <div className="input-flex row">
              <div className="col-sm-6">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. 1234 Main St."
                  type="text"
                  label="Home Address"
                  value={values.home_address}
                  name="home_address"
                  className="mb-1"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="home_address"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <AsyncSelect
                  label="Home State"
                  isRequired
                  options={statesList?.map((el) => ({
                    value: el.id,
                    label: el.name.toUpperCase(),
                  }))}
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedStatesDataCallback(inputValue, resolve);
                    })
                  }
                  selectedValue={values.state_id}
                  placeholder="Select State"
                  handleChange={(obj) => {
                    setFieldValue("state_id", obj);
                    setFieldValue("county_id", "");
                    setFieldValue("city_id", "");
                    setFieldValue("zip_code_id", "");
                    fetchCounties(obj.value);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="state_id"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <AsyncSelect
                  selectedValue={values.county_id}
                  label="Home County"
                  isRequired
                  disabled={values.state_id ? false : true}
                  options={countiesList?.map((el) => ({
                    value: el.id,
                    label: formatLocation(el.name),
                  }))}
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedCountiesDataCallback(
                        inputValue,
                        values.state_id?.value,
                        resolve
                      );
                    })
                  }
                  placeholder="Select County"
                  handleChange={(obj) => {
                    setFieldValue("county_id", obj);
                    setFieldValue("city_id", "");
                    setFieldValue("zip_code_id", "");
                    fetchCities(values.state_id?.value, obj.value);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="county_id"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <AsyncSelect
                  label="Home City"
                  disabled={values.county_id ? false : true}
                  options={citiesList.map((el) => ({
                    value: el.id,
                    label: capitalizeName(el.name),
                  }))}
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedCitiesDataCallback(
                        inputValue,
                        values.state_id?.value,
                        values.county_id?.value,
                        resolve
                      );
                    })
                  }
                  selectedValue={values?.city_id}
                  placeholder="Select City"
                  handleChange={(obj) => {
                    setFieldValue("city_id", obj);
                    setFieldValue("zip_code_id", "");
                    fetchZipCodes(
                      values.state_id?.value,
                      values?.county_id?.value,
                      obj.value
                    );
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="city_id"
                    />
                  }
                />
              </div>
              <div>
                <AsyncSelect
                  name="zip_code_id"
                  label="Home ZIP Code"
                  disabled={values?.city_id ? false : true}
                  options={zipCodesList?.map((el) => ({
                    value: el.id,
                    label: el.code,
                  }))}
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedZipCodesDataCallback(
                        inputValue,
                        values.state_id?.value,
                        values.county_id?.value,
                        values.city_id?.value,
                        resolve
                      );
                    })
                  }
                  selectedValue={values?.zip_code_id}
                  handleChange={(obj) => {
                    setFieldValue("zip_code_id", obj);
                  }}
                  className="mb-1"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="zip_code_id"
                    />
                  }
                />
              </div>
              {/* Checkbox for syncing mailing address with home address */}
              <div className="col-sm-12 mb-3">
                <label className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    checked={isMailingSameAsHome}
                    onChange={(e) => {
                      const isChecked = e.target.checked;

                      if (
                        values.home_address &&
                        values.state_id &&
                        values.county_id
                      ) {
                        setIsMailingSameAsHome(isChecked);
                        if (isChecked) {
                          setMailingStatesList(statesList);
                          setMailingCountiesList(countiesList);
                          setMailingCitiesList(citiesList);
                          setMailingZipCodesList(zipCodesList);
                          setFieldValue("mailing_address", values.home_address);
                          setFieldValue("mailing_state_id", values.state_id);
                          setFieldValue("mailing_county_id", values.county_id);
                          setFieldValue("mailing_city_id", values.city_id);
                          setFieldValue(
                            "mailing_zip_code_id",
                            values.zip_code_id
                          );
                        } else {
                          setMailingCountiesList([]);
                          setMailingCitiesList([]);
                          setMailingZipCodesList([]);
                          setFieldValue("mailing_address", "");
                          setFieldValue("mailing_state_id", "");
                          setFieldValue("mailing_county_id", "");
                          setFieldValue("mailing_city_id", "");
                          setFieldValue("mailing_zip_code_id", "");
                        }
                      } else {
                        toast(
                          <CustomToast
                            variant={alertTypes.INFO}
                            message="Fill Home  Address, State and County!"
                          />
                        );
                      }
                    }}
                    className="me-2 form-check-input light-grey-background"
                  />
                  Mailing Address same as Home Address
                </label>
              </div>

              {!isMailingSameAsHome && (
                <>
                  <div className="col-sm-6">
                    <Input
                      isRequired
                      handleChange={handleChange}
                      placeholder="e.g. 1234 Main St."
                      type="text"
                      label="Mailing Address"
                      value={values.mailing_address}
                      name="mailing_address"
                      className="mb-1"
                      disabled={isMailingSameAsHome}
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="mailing_address"
                        />
                      }
                    />
                  </div>
                  <div className="col-sm-6">
                    <AsyncSelect
                      label="Mailing State"
                      isRequired
                      options={mailingStatesList?.map((el) => ({
                        value: el.id,
                        label: el.name.toUpperCase(),
                      }))}
                      loadOptions={(inputValue) =>
                        new Promise((resolve) => {
                          debouncedStatesDataCallback(inputValue, resolve);
                        })
                      }
                      selectedValue={values.mailing_state_id}
                      placeholder="Select State"
                      handleChange={(obj) => {
                        setFieldValue("mailing_state_id", obj);
                        setFieldValue("mailing_county_id", "");
                        setFieldValue("mailing_city_id", "");
                        setFieldValue("mailing_zip_code_id", "");
                        fetchMailingCounties(obj.value);
                      }}
                      disabled={isMailingSameAsHome}
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="mailing_state_id"
                        />
                      }
                    />
                  </div>
                  <div className="col-sm-6">
                    <AsyncSelect
                      selectedValue={values.mailing_county_id}
                      label="Mailing County"
                      isRequired
                      disabled={isMailingSameAsHome || !values.mailing_state_id}
                      options={mailingCountiesList?.map((el) => ({
                        value: el.id,
                        label: formatLocation(el.name),
                      }))}
                      loadOptions={(inputValue) =>
                        new Promise((resolve) => {
                          debouncedCountiesDataCallback(
                            inputValue,
                            values.mailing_state_id?.value,
                            resolve
                          );
                        })
                      }
                      placeholder="Select County"
                      handleChange={(obj) => {
                        setFieldValue("mailing_county_id", obj);
                        setFieldValue("mailing_city_id", "");
                        setFieldValue("mailing_zip_code_id", "");
                        fetchMailingCities(
                          values.mailing_state_id?.value,
                          obj.value
                        );
                      }}
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="mailing_county_id"
                        />
                      }
                    />
                  </div>
                  <div className="col-sm-6">
                    <AsyncSelect
                      label="Mailing City"
                      disabled={
                        isMailingSameAsHome || !values.mailing_county_id
                      }
                      options={mailingCitiesList.map((el) => ({
                        value: el.id,
                        label: capitalizeName(el.name),
                      }))}
                      loadOptions={(inputValue) =>
                        new Promise((resolve) => {
                          debouncedCitiesDataCallback(
                            inputValue,
                            values.mailing_state_id?.value,
                            values.mailing_county_id?.value,
                            resolve
                          );
                        })
                      }
                      selectedValue={values?.mailing_city_id}
                      placeholder="Select City"
                      handleChange={(obj) => {
                        setFieldValue("mailing_city_id", obj);
                        setFieldValue("mailing_zip_code_id", "");
                        fetchMailingZipCodes(
                          values.mailing_state_id?.value,
                          values.mailing_county_id?.value,
                          obj.value
                        );
                      }}
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="mailing_city_id"
                        />
                      }
                    />
                  </div>
                  <div>
                    <AsyncSelect
                      name="mailing_zip_code_id"
                      label="Mailing ZIP Code"
                      disabled={isMailingSameAsHome || !values.mailing_city_id}
                      options={mailingZipCodesList?.map((el) => ({
                        value: el.id,
                        label: el.code,
                      }))}
                      loadOptions={(inputValue) =>
                        new Promise((resolve) => {
                          debouncedZipCodesDataCallback(
                            inputValue,
                            values.mailing_state_id?.value,
                            values.mailing_county_id?.value,
                            values.mailing_city_id?.value,
                            resolve
                          );
                        })
                      }
                      selectedValue={values?.mailing_zip_code_id}
                      handleChange={(obj) => {
                        setFieldValue("mailing_zip_code_id", obj);
                      }}
                      className="mb-1"
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="mailing_zip_code_id"
                        />
                      }
                    />
                  </div>
                </>
              )}
            </div>
            <h4 className="my-4">Communication Preferences</h4>
            <div className="input-flex row">
              <div className="col-sm-6">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. English"
                  type="text"
                  label="Preferred Language Spoken"
                  value={values.preferred_spoken_lang}
                  name="preferred_spoken_lang"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="preferred_spoken_lang"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. Chinese"
                  type="text"
                  label="Preferred Language Written"
                  value={values.preferred_written_lang}
                  name="preferred_written_lang"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="preferred_written_lang"
                    />
                  }
                />
              </div>
            </div>
            <h4 className="my-4">Other</h4>
            <div className="input-flex row">
              <div className="col-sm-6">
                <SelectComponent
                  isMulti
                  isRequired
                  name="population_focus"
                  label="Population of Focus"
                  options={POPULATION_FOCUSES}
                  selectedValue={values.population_focus}
                  placeholder="Select Population of Focus"
                  handleChange={(arr) => {
                    setFieldValue("population_focus", arr);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="population_focus"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <AsyncSelect
                  name="conditions"
                  label="Condition"
                  options={CONDITIONS}
                  selectedValue={values.conditions}
                  isMulti
                  isRequired
                  placeholder="Select Condition"
                  handleChange={(arr) => {
                    setFieldValue("conditions", arr);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="conditions"
                    />
                  }
                />
              </div>
            </div>
            <h4 className="my-4">Referral Information</h4>
            {selectedMember?.member_info_jsonb?.member?.addresses_attributes?.find(
              (a) =>
                a.address_type === "mailing" &&
                a.county_id == process.env.REACT_APP_COUNTY_ID
            ) && (
              <div className="input-flex row">
                <div className="col-sm-6">
                  <h6>SPA Code:</h6>
                </div>
                <div className="col-sm-6">
                  {selectedMember?.member?.spa_code ===
                  "Zip Code does not have assigned SPA Number" ? (
                    <p className="error-message">
                      {selectedMember?.member?.spa_code}
                    </p>
                  ) : (
                    <p>{selectedMember?.member?.spa_code}</p>
                  )}
                </div>
              </div>
            )}
            <div className="input-flex row">
              <div className="col-sm-6">
                <SelectComponent
                  isRequired
                  label="Referral Status"
                  name="referral_status"
                  options={Object.keys(REFERRAL_STATUS).map((key) => ({
                    label: key,
                    value: REFERRAL_STATUS[key],
                  }))}
                  selectedValue={values.referral_status}
                  handleChange={(obj) =>
                    setFieldValue("referral_status", obj.value)
                  }
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="referral_status"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <AsyncSelect
                  isRequired
                  label="Existing Program Enrollments"
                  name="programe_enrollement"
                  options={programTypes}
                  isMulti
                  selectedValue={values.programe_enrollement}
                  handleChange={(arr) =>
                    setFieldValue("programe_enrollement", arr)
                  }
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="programe_enrollement"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <AsyncSelect
                  isRequired
                  label="Referral Source"
                  isMulti={false}
                  options={organizationList?.map((el) => ({
                    ...el,
                    value: el.id,
                    label: el.name,
                  }))}
                  dataCallback={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedFetchOrganizationData(inputValue, (options) => {
                        if (!options || options.length === 0) {
                          resolve([]);
                        } else {
                          resolve(options);
                        }
                      });
                    })
                  }
                  placeholder="Select Referral"
                  handleChange={(selectedOption) => {
                    setSelectedReferralSource(selectedOption);
                    setFieldValue(
                      "referral_source",
                      selectedOption?.label || ""
                    );
                    setFieldValue(
                      "community_engagement_id",
                      selectedOption?.value || ""
                    );
                    setFieldValue("referring_provider", "");
                    setFieldValue("referral_contact_id", "");
                  }}
                  selectedValue={
                    values.referral_source
                      ? {
                          value: values.community_engagement_id,
                          label: values.referral_source,
                        }
                      : null
                  }
                  name="referral_source"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="referral_source"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <AsyncSelect
                  isRequired
                  label="Referral Provider"
                  isMulti={false}
                  options={
                    selectedReferralSource?.organization_contacts?.map(
                      (contact) => ({
                        value: contact.id,
                        label: contact.name,
                      })
                    ) || []
                  }
                  dataCallback={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedFetchOrganizationData(inputValue, (options) => {
                        const filteredOptions = options
                          .filter(
                            (org) =>
                              org.value === values.community_engagement_id
                          )
                          .flatMap(
                            (org) =>
                              organizationList
                                .find((o) => o.id === org.value)
                                ?.organization_contacts.map((contact) => ({
                                  value: contact.id,
                                  label: contact.name,
                                })) || []
                          );
                        resolve(filteredOptions);
                      });
                    })
                  }
                  placeholder="Select..."
                  handleChange={(selectedOption) => {
                    setFieldValue(
                      "referring_provider",
                      selectedOption?.label || ""
                    );
                    setFieldValue(
                      "referral_contact_id",
                      selectedOption?.value || ""
                    );
                  }}
                  selectedValue={
                    values.referring_provider
                      ? {
                          value: values.referral_contact_id,
                          label: values.referring_provider,
                        }
                      : null
                  }
                  name="referring_provider"
                  isDisabled={!selectedReferralSource}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="referring_provider"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <SelectComponent
                  isRequired
                  label="Referral Program"
                  name="referral_programe"
                  options={programTypes}
                  selectedValue={values.referral_programe}
                  handleChange={(obj) =>
                    setFieldValue("referral_programe", obj.value)
                  }
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="referral_programe"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <Input
                  handleChange={handleChange}
                  placeholder="e.g. Number"
                  type="text"
                  label="IF Number"
                  value={values.referral_if_number}
                  name="referral_if_number"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="referral_if_number"
                    />
                  }
                />
              </div>
              {values.referral_status === REFERRAL_STATUS.Denied && (
                <div className="col-sm-6">
                  <Input
                    isRequired
                    handleChange={handleChange}
                    placeholder="Reason..."
                    type="text"
                    label="Denial Reason"
                    value={values.denial_reason}
                    name="denial_reason"
                    error={
                      <ErrorMessage
                        className="error-message"
                        component="p"
                        name="denial_reason"
                      />
                    }
                  />
                </div>
              )}
              <div className="col-sm-12">
                <SelectComponent
                  label="Current Living Situation"
                  isRequired
                  options={CURRENT_LIVING_SITUATION}
                  selectedValue={values.current_living_situation}
                  placeholder="Current Living Situation"
                  handleChange={(obj) => {
                    setFieldValue("current_living_situation", obj.value);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="current_living_situation"
                    />
                  }
                />
              </div>
            </div>
            <div className="col-sm-12">
              <Input
                type="file"
                accept=".pdf,.png,.jpeg,.jpg"
                handleChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setFieldValue("attachment", file);
                  setFieldValue("file_name", file?.name);
                  setFieldValue("content_type", file?.type);
                }}
                value={undefined}
                label="Attachment"
                name="attachment"
                className="mb-1"
              />
              {
                <ErrorMessage
                  style={{
                    fontSize: "0.775rem",
                    color: "red",
                  }}
                  component="p"
                  name="attachment"
                />
              }
            </div>
            {values.referral_status === REFERRAL_STATUS.Approved && (
              <div>
                <h4 className="my-4">Authorization Information</h4>
                <div className="input-flex row">
                  <div className="col-sm-6">
                    <Input
                      handleChange={handleChange}
                      placeholder="Auth Number..."
                      type="text"
                      isRequired
                      label="Authorization Number"
                      value={values.auth_number}
                      name="auth_number"
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="auth_number"
                        />
                      }
                    />
                  </div>
                  <div className="col-sm-6">
                    <DateInput
                      handleChange={(date) => {
                        setFieldValue("start_date", date);
                      }}
                      value={values.start_date}
                      isRequired
                      label="Authorization Start Date"
                      name="start_date"
                      placeholder="Auth Start Date.."
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="start_date"
                        />
                      }
                    />
                  </div>
                  <div className="col-sm-6">
                    <DateInput
                      handleChange={(date) => {
                        setFieldValue("end_date", date);
                      }}
                      value={values.end_date}
                      isRequired
                      label="Authorization End Date"
                      name="end_date"
                      placeholder="Auth End Date.."
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="end_date"
                        />
                      }
                    />
                  </div>
                  <div className="col-sm-6">
                    <Input
                      handleChange={handleChange}
                      isRequired
                      placeholder="No. of Approved Days.."
                      type="text"
                      label="Number of Approved Days"
                      value={values.approved_days}
                      name="approved_days"
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="approved_days"
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            )}
            {/* <AddressSync
              isMailingSameAsHome={isMailingSameAsHome}
              fetchMailingCounties={fetchMailingCounties}
              fetchMailingCities={fetchMailingCities}
              fetchMailingZipCodes={fetchMailingZipCodes}
              mailingCountiesList={mailingCountiesList}
              mailingCitiesList={mailingCitiesList}
              mailingZipCodesList={mailingZipCodesList}
            /> */}
            <hr className="custom-modal-line mt-4" />
            <div className="action gap-2">
              <button
                className="secondary-btn w-50"
                type="button"
                onClick={handleClose}
              >
                Cancel
              </button>
              <input
                className="primary-btn w-50"
                type="submit"
                value="Add Referral"
              />
            </div>
          </form>
        </div>
      )}
    </Formik>
  );
};

export default AddFormWithoutMember;
